<script context="module">
    import { selectedItem } from '../../../store/store.js';
    import { displayInt, tooltip } from '../../../utils/utils.js';

    function displayNumberOfApartments(itemData) {
		// Check if number_of_apartments exists at the top level
		if (itemData.number_of_apartments) {
			return itemData.number_of_apartments;
		}

		if (itemData.data === null) {
			return "Uppgifter saknas"
		}

		// Check for apartments in the nested data array
		if (itemData.data) {
			for (const element of itemData.data) {
				if (element.apartments) {
					return element.apartments;
				}
			}
		}

		// Return a fallback value if no apartments are found
		return "-";
	}

    // Helper function to display optional values or a dash
    function displayOptionalInt(value) {
        return value != null ? displayInt(value) : '-';
    }
    
    // Helper function to display optional booleans or a specific string
    function displayOptionalBoolean(value, trueText = 'Ja', falseText = 'Nej', nullText = '-') {
        if (value === true) return trueText;
        if (value === false) return falseText;
        return nullText;
    }
</script>

<div class="key-value-table box">
    <div class="header-card">
        <h5><i class="fa-solid fa-building fa-sm icon-detail"></i> Fastighet</h5>
    </div>
    <div class="metrics-grid">
        <!-- Antal lägenheter -->
        {#if $selectedItem?.number_of_apartments || $selectedItem?.data}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-building fa-sm icon-detail"></i> Antal lägenheter</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p>Det finns ingen exakt regel för vad som gör en bostadsrättsförening med få lägenheter "bra" eller "dålig", men här är några allmänna riktlinjer:</p>
                            <p><strong>Sammanfattning av vad man bör tänka på:</strong></p>
                            <ul>
                                <li><strong>Fördelar med få lägenheter:</strong> Nära gemenskap, större individuellt inflytande, snabbare beslutsprocesser.</li>
                                <li><strong>Nackdelar med få lägenheter:</strong> Ekonomisk sårbarhet, mer administrativt ansvar per medlem, färre kompetenser i föreningen.</li>
                            </ul>
                            <p><strong>Storleksintervall för bostadsrättsföreningar:</strong></p>
                            <ul>
                                <li><strong>Mycket små föreningar (2–10 lägenheter):</strong></li>
                                <ul>
                                    <li><strong>Fördel:</strong> Liten och tight gemenskap, snabba beslut.</li>
                                    <li><strong>Nackdel:</strong> Stor ekonomisk risk för enskilda medlemmar vid större reparationer.</li>
                                </ul>
                                <li><strong>Små föreningar (11–30 lägenheter):</strong></li>
                                <ul>
                                    <li><strong>Fördel:</strong> Bättre balans mellan gemenskap och ekonomisk risk.</li>
                                    <li><strong>Nackdel:</strong> Mindre ekonomisk flexibilitet jämfört med större föreningar.</li>
                                </ul>
                                <li><strong>Medelstora föreningar (31–100 lägenheter):</strong></li>
                                <ul>
                                    <li><strong>Fördel:</strong> Högre ekonomisk stabilitet och diversifierad kompetens.</li>
                                    <li><strong>Nackdel:</strong> Mindre individuell påverkan jämfört med mindre föreningar.</li>
                                </ul>
                                <li><strong>Stora föreningar (över 100 lägenheter):</strong></li>
                                <ul>
                                    <li><strong>Fördel:</strong> Större ekonomisk trygghet och professionell förvaltning.</li>
                                    <li><strong>Nackdel:</strong> Mindre gemenskap och längre beslutsvägar.</li>
                                </ul>
                            </ul>
                            <p><strong>Detaljerad bedömning:</strong></p>
                            <p>En liten förening kan vara ett bra val om du värderar närhet och inflytande, men den ekonomiska sårbarheten bör granskas noggrant.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    { displayNumberOfApartments($selectedItem) } st
                </div>
            </div>
        {/if}

        <!-- Total boyta -->
        {#if $selectedItem?.living_area_sqm}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-ruler-combined fa-sm icon-detail"></i> Total boyta</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p>Om den totala boytan ej anges i årsredovisningen har denna siffra beräknats genom att dividera Totala Avgiftsinkomsten med Avgift per kvm. </p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    { displayInt($selectedItem.living_area_sqm) } kvm
                </div>
            </div>
        {/if}

        <!-- Total lokalyta -->
        {#if $selectedItem?.commercial_area_sqm}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-ruler-combined fa-sm icon-detail"></i> Total lokalyta</h6>
                </div>
                <div class="metric-value">
                    { displayInt($selectedItem.commercial_area_sqm) } kvm
                </div>
            </div>
        {/if}

        <!-- Total markyta -->
        {#if $selectedItem?.land_area}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-ruler-combined fa-sm icon-detail"></i> Total markyta</h6>
                </div>
                <div class="metric-value">
                    { displayInt($selectedItem.land_area) } kvm
                </div>
            </div>
        {/if}
        
        <!-- Byggår -->
        {#if $selectedItem?.nybyggnadsar}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-calendar-days fa-sm icon-detail"></i> Byggår</h6>
                </div>
                <div class="metric-value">
                    { $selectedItem.nybyggnadsar }
                </div>
            </div>
        {/if}

        <!-- Antal hyresrätter -->
        {#if $selectedItem?.number_of_rental_apartments != null}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-building fa-sm icon-detail"></i> Antal hyresrätter</h6>
                </div>
                <div class="metric-value">
                    { displayOptionalInt($selectedItem.number_of_rental_apartments) } st
                </div>
            </div>
        {/if}
        
        <!-- Antal P-platser -->
        {#if $selectedItem?.number_of_parking_spots != null}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-square-parking fa-sm icon-detail"></i> Antal P-platser</h6>
                </div>
                <div class="metric-value">
                    { displayOptionalInt($selectedItem.number_of_parking_spots) } st
                </div>
            </div>
        {/if}

        <!-- Råvind -->
        {#if $selectedItem?.raw_attic != null}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-house-chimney fa-sm icon-detail"></i> Råvind finns</h6>
                </div>
                <div class="metric-value">
                    { displayOptionalBoolean($selectedItem.raw_attic) }
                </div>
            </div>
        {/if}

        <!-- Cykelrum -->
        {#if $selectedItem?.bike_storage != null}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-bicycle fa-sm icon-detail"></i> Cykelrum finns</h6>
                </div>
                <div class="metric-value">
                    { displayOptionalBoolean($selectedItem.bike_storage) }
                </div>
            </div>
        {/if}

        <!-- Taxeringsvärde -->
        {#if $selectedItem?.taxerings_value}
            <div class="metric-card">
                <div class="metric-header">
                    <h6>Taxeringsvärde</h6>
                </div>
                <div class="metric-value">
                    { displayInt($selectedItem.taxerings_value) } kr
                </div>
            </div>
        {/if}

        <!-- Taxeringsvärde mark -->
        {#if $selectedItem?.assessed_value_of_the_land}
            <div class="metric-card">
                <div class="metric-header">
                    <h6>Taxeringsvärde mark</h6>
                </div>
                <div class="metric-value">
                    { displayInt($selectedItem.assessed_value_of_the_land) } kr
                </div>
            </div>
        {/if}
    </div>
</div>

<style>

</style>